import { X } from "lucide-react";
import { useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/cn";
import { useFocusTrap } from "@/lib/useFocusTrap";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** `sm` for a confirmation, `md` for a form, `lg` for a form beside a summary. */
  size?: "sm" | "md" | "lg";
}

/**
 * A modal dialog.
 *
 * Hand-rolled rather than pulled from a library, for the same reason as the
 * doughnut: one dialog does not justify a dependency, and this one has to behave
 * on a till. What it must get right is the part people skip —
 *
 * *   **Focus goes in and stays in.** A cashier tabbing past the last field must
 *     not land on the product table behind the dialog, still less submit
 *     something there.
 * *   **Escape closes it, and so does the backdrop.** Both are muscle memory.
 * *   **Focus returns** to whatever opened it, so the keyboard does not jump to
 *     the top of the page afterwards.
 * *   **The page behind does not scroll.** On a touchscreen a stray drag
 *     scrolling the list underneath is disorienting.
 *
 * Rendered through a portal so the dialog is not trapped inside the table's
 * `overflow-x: auto` container, which would clip it.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
}: DialogProps) {
  const panel = useRef<HTMLDivElement>(null);

  // Focus in, focus trapped, Escape closes, focus back — see `useFocusTrap`.
  // Shared with the navigation drawer rather than written twice, because the
  // parts of this people skip are the parts that matter.
  useFocusTrap({ active: open, panel, onClose });

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      {/* The backdrop is a button so that dismissing by clicking away is a real
          control rather than a click handler on a div — it is reachable, it is
          announced, and it needs no ARIA apology. */}
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-navy-900/50"
      />

      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          "relative flex max-h-[90vh] w-full flex-col rounded-t-card bg-surface shadow-card",
          "sm:rounded-card",
          size === "sm"
            ? "sm:max-w-sm"
            : size === "lg"
              ? "sm:max-w-3xl"
              : "sm:max-w-lg",
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-line p-5">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ink-900">{title}</h2>
            {description && (
              <p className="mt-0.5 text-sm text-ink-500">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid size-9 shrink-0 place-items-center rounded-control text-ink-500 hover:bg-canvas hover:text-ink-900"
          >
            <X className="size-4" aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>

        {footer && (
          <footer className="flex flex-wrap justify-end gap-2 border-t border-line p-5">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
